/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/store';

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
const LAST_ACTIVITY_KEY = 'aegis-last-activity';

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

// Public pages where inactivity should NOT trigger a login redirect
const PUBLIC_PAGE_PATHS = [
  '/rental-agreement/',
  '/create-user-and-agreement/',
  '/add-card/',
  '/link-expired',
  '/create-user',
  '/create-owner',
  '/terms',
  '/rental-terms',
];

function performLogout() {
  // Don't redirect to login from public pages
  const isPublicPage = PUBLIC_PAGE_PATHS.some(path => window.location.pathname.startsWith(path));
  if (isPublicPage) return;

  localStorage.removeItem(LAST_ACTIVITY_KEY);
  useAuthStore.getState().logout();
  // Suppress beforeunload dialog — session expired, unsaved data is lost anyway
  window.onbeforeunload = null;
  window.location.href = '/login';
}

export function useInactivityTimeout() {
  const { isAuthenticated } = useAuthStore();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    // Record last activity timestamp
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(performLogout, INACTIVITY_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Check if session expired while browser was closed
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        performLogout();
        return;
      }
      // Set timer for remaining time
      const remaining = INACTIVITY_TIMEOUT_MS - elapsed;
      timerRef.current = setTimeout(performLogout, remaining);
    } else {
      // First activity — start fresh
      resetTimer();
    }

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, resetTimer, { passive: true });
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, resetTimer);
      }
    };
  }, [isAuthenticated, resetTimer]);
}
